/**
 * MonthlyTrackingGrid — F-016 (2026-05-22) · candy redesign 2026-05-22.
 *
 * Full-width Hero-Dashboard card. Shows the current calendar month as a
 * heatmap-style grid where rows are KPIs + Hero Tracker items, columns
 * are days 1..N of the current month.
 *
 * Visual sections per group:
 *   - KPI       → 3 rows (Energy / Work / Love), warm parchment tint
 *                  + flame-deep eyebrow ribbon
 *   - AM bookend → flame-glow tint + sunrise ribbon
 *   - Anytime   → cream-bone tint + neutral ribbon
 *   - PM bookend → ember tint + sunset ribbon
 *
 * Cell treatment:
 *   - Yes/No "done" → filled flame gem with a soft glow halo
 *   - Yes/No "not done" → soft empty ring (bone-3)
 *   - Number > 0 → mono digit on a faint flame-glow pill
 *   - Number = 0 / no value → blank
 *   - KPI value → mono digit, color-tinted by value (low=ember, mid=flame, high=flame-deep)
 *   - Today's column → vertical flame-glow band + brand ring on each cell
 *   - Item not active that day → muted dot (·)
 *
 * Layout:
 *   - Sticky-left label column (180px) so the user always knows which
 *     row they're scrolling across.
 *   - Sticky-top day-number header.
 *   - Horizontal scroll on narrow screens.
 *   - Each group is a separate visual section with its own background
 *     band — the grid reads as four candy stripes stacked vertically.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TrackingItem, TrackingValue, ClientDashboard, VirtueAxis } from '@/lib/api-client';
import { AxisIcon } from '@/components/axis-icon';

type Category = 'am_bookend' | 'pm_bookend' | 'anywhere';
type KpiTrendPoint = ClientDashboard['kpiTrend30d'][number];

interface MonthlyTrackingGridProps {
  items: TrackingItem[];
  values: TrackingValue[];
  kpiTrend: KpiTrendPoint[];
  todayIso: string; // YYYY-MM-DD
  /** #16 — target KPIs from the Step-1 Compass; rendered in brackets after
   *  each KPI row label (e.g. "Energy (8)"). Null/undefined → no bracket. */
  targetKpis?: { energy: number; work: number; love: number } | null;
}

interface GroupVisual {
  key: 'kpi' | Category;
  labelKey: string;
  // Small badge icon for the ribbon.
  glyph: string;
}

// KPI keeps a brighter orange so the summary group reads as the
// headline. AM / Anytime / PM share a single flame-ember badge — one
// consistent rhythm for the three tracker groups.
const RIBBON_CLASS_KPI = 'bg-flame text-bone';
const RIBBON_CLASS_TRACKER = 'bg-flame-ember text-bone';

// Single shared alternating-row tint — same lighter shade across every
// section (was per-section accent before).
const BAND_CLASS = 'bg-flame-glow/15';

const GROUP_VISUALS: readonly GroupVisual[] = [
  {
    key: 'kpi',
    labelKey: 'dashboard.monthlyGrid.group.kpi',
    glyph: '★',
  },
  {
    key: 'am_bookend',
    labelKey: 'dashboard.monthlyGrid.group.am_bookend',
    glyph: '☼',
  },
  {
    key: 'anywhere',
    labelKey: 'dashboard.monthlyGrid.group.anywhere',
    glyph: '◆',
  },
  {
    key: 'pm_bookend',
    labelKey: 'dashboard.monthlyGrid.group.pm_bookend',
    glyph: '☾',
  },
] as const;

function monthDays(todayIso: string): { days: string[]; today: string } {
  const today = new Date(`${todayIso}T00:00:00.000Z`);
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const days: string[] = [];
  for (let d = 1; d <= lastDay; d++) {
    days.push(new Date(Date.UTC(year, month, d)).toISOString().slice(0, 10));
  }
  return { days, today: todayIso };
}

function isActiveOn(
  item: Pick<TrackingItem, 'createdAt' | 'archivedAt'>,
  dayIso: string,
): boolean {
  const dayEnd = new Date(`${dayIso}T23:59:59.999Z`).getTime();
  if (new Date(item.createdAt).getTime() > dayEnd) return false;
  if (item.archivedAt) {
    const dayStart = new Date(`${dayIso}T00:00:00.000Z`).getTime();
    if (new Date(item.archivedAt).getTime() < dayStart) return false;
  }
  return true;
}

function kpiCellClass(value: number | null): string {
  // Color-tint per KPI value to give the grid a candy gradient feel
  // without overwhelming. Low values lean ember (deeper), high lean flame.
  if (value === null) return 'text-gray-1';
  if (value >= 8) return 'text-flame-deep font-semibold';
  if (value >= 5) return 'text-flame-ember';
  return 'text-gray-2';
}

export function MonthlyTrackingGrid({
  items,
  values,
  kpiTrend,
  todayIso,
  targetKpis,
}: MonthlyTrackingGridProps): JSX.Element {
  const { t } = useTranslation();
  const { days, today } = useMemo(() => monthDays(todayIso), [todayIso]);

  const valuesByDateAndItem = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    for (const v of values) {
      const day = m.get(v.date);
      if (day) day.set(v.trackingItemId, v.value);
      else m.set(v.date, new Map([[v.trackingItemId, v.value]]));
    }
    return m;
  }, [values]);

  const kpiByDate = useMemo(() => {
    const m = new Map<string, KpiTrendPoint>();
    for (const p of kpiTrend) m.set(p.date, p);
    return m;
  }, [kpiTrend]);

  const itemsByCategory = useMemo(() => {
    const buckets: Record<Category, TrackingItem[]> = {
      am_bookend: [],
      anywhere: [],
      pm_bookend: [],
    };
    for (const item of items) {
      if (item.archivedAt) continue;
      buckets[item.category].push(item);
    }
    for (const key of Object.keys(buckets) as Category[]) {
      buckets[key].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return buckets;
  }, [items]);

  const dayLabels = days.map((iso) => Number.parseInt(iso.slice(-2), 10));
  const todayIndex = days.indexOf(today);

  // Day-of-week for each day, used as a tiny letter above the date
  // (M / T / W / T / F / S / S) to help spot weekly patterns at a glance.
  const dowLabels = useMemo(() => {
    return days.map((iso) => {
      const d = new Date(`${iso}T00:00:00.000Z`).getUTCDay();
      return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d] ?? '';
    });
  }, [days]);

  return (
    <article
      className="bg-bone-2 rounded-xl p-s5 border border-bone-3 shadow-md overflow-hidden"
      data-testid="card-monthly-grid"
    >
      <header className="mb-s5 flex items-end justify-between gap-s4">
        <div>
          <h2 className="font-display text-display-sm text-ink">
            {t('dashboard.monthlyGrid.title')}
          </h2>
          <p className="mt-s1 text-sm text-gray-2 max-w-prose">
            {t('dashboard.monthlyGrid.subtitle')}
          </p>
        </div>
        <Legend />
      </header>
      <div className="overflow-x-auto rounded-lg border border-bone-3 bg-bone">
        <table
          className="w-full border-separate border-spacing-0 text-xs"
          // table-fixed + explicit per-column widths so every day column
          // is the same width regardless of cell content (a "30" digit
          // no longer pushes columns wider than a "1" digit). Sticky
          // label column anchored at 200px.
          style={{ tableLayout: 'fixed' }}
          data-testid="monthly-grid-table"
        >
          <colgroup>
            <col style={{ width: '200px' }} />
            {dayLabels.map((d) => (
              <col key={`col-${d}`} style={{ width: '28px' }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20 bg-bone text-left pl-s4 pr-s3 pt-s3 pb-s2 border-b border-bone-3"
                rowSpan={2}
                aria-label={t('dashboard.monthlyGrid.itemColumn')}
              />
              {dayLabels.map((d, idx) => (
                <th
                  key={`dow-${d}`}
                  className={`text-center font-mono text-[9px] uppercase tracking-wider pt-s2 pb-1 ${
                    idx === todayIndex ? 'text-flame-deep' : 'text-gray-1'
                  }`}
                >
                  {dowLabels[idx]}
                </th>
              ))}
            </tr>
            <tr>
              {dayLabels.map((d, idx) => (
                <th
                  key={d}
                  className={`text-center font-mono text-xs pb-s2 ${
                    idx === todayIndex
                      ? 'bg-flame-glow/60 text-flame-deep font-bold rounded-t-md'
                      : 'text-gray-2'
                  }`}
                  data-testid={`monthly-grid-day-${d}`}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUP_VISUALS.map((group) => {
              if (group.key === 'kpi') {
                return (
                  <KpiGroup
                    key="kpi"
                    group={group}
                    days={days}
                    todayIndex={todayIndex}
                    kpiByDate={kpiByDate}
                    targetKpis={targetKpis ?? null}
                  />
                );
              }
              const groupItems = itemsByCategory[group.key];
              return (
                <TrackerGroup
                  key={group.key}
                  group={group}
                  emptyLabel={t('dashboard.monthlyGrid.emptyRow')}
                  items={groupItems}
                  days={days}
                  todayIndex={todayIndex}
                  valuesByDateAndItem={valuesByDateAndItem}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function Legend(): JSX.Element {
  const { t } = useTranslation();
  // "Today" entry removed 2026-05-22 — the vertical flame-glow bar in
  // the grid is self-explaining; the legend only needs done / not-done.
  return (
    <div className="hidden md:flex items-center gap-s3 text-[11px] text-gray-2">
      <span className="flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-full bg-flame shadow-[0_0_6px_rgba(255,131,0,0.6)]" />
        {t('dashboard.monthlyGrid.legend.done')}
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-full border border-bone-3 bg-bone-2" />
        {t('dashboard.monthlyGrid.legend.notDone')}
      </span>
    </div>
  );
}

function GroupRibbon({
  group,
  days,
  todayIndex,
}: {
  group: GroupVisual;
  days: string[];
  todayIndex: number;
}): JSX.Element {
  const { t } = useTranslation();
  const ribbonClass = group.key === 'kpi' ? RIBBON_CLASS_KPI : RIBBON_CLASS_TRACKER;
  // Per-day cells (instead of a single colSpan) so today's vertical
  // flame-glow band stays an UNINTERRUPTED bar through the ribbon row.
  return (
    <tr>
      <td className="sticky left-0 z-10 bg-bone px-0 pt-s3 pb-0 border-t border-bone-3">
        <div className="flex items-center gap-s2">
          <span
            className={`inline-flex items-center gap-1 ${ribbonClass} font-mono uppercase text-[10px] tracking-wider px-s3 py-1 rounded-r-full`}
          >
            <span className="text-[12px] leading-none">{group.glyph}</span>
            {t(group.labelKey)}
          </span>
        </div>
      </td>
      {days.map((_iso, idx) => (
        <td
          key={`${group.key}-ribbon-${idx}`}
          className={`pt-s3 pb-0 border-t border-bone-3 ${
            idx === todayIndex ? 'bg-flame-glow/30' : ''
          }`}
          aria-hidden
        />
      ))}
    </tr>
  );
}

function KpiGroup({
  group,
  days,
  todayIndex,
  kpiByDate,
  targetKpis,
}: {
  group: GroupVisual;
  days: string[];
  todayIndex: number;
  kpiByDate: Map<string, KpiTrendPoint>;
  targetKpis: { energy: number; work: number; love: number } | null;
}): JSX.Element {
  const { t } = useTranslation();
  const rows = [
    { key: 'energy' as const, labelKey: 'dashboard.monthlyGrid.kpi.energy', target: targetKpis?.energy ?? null },
    { key: 'work' as const, labelKey: 'dashboard.monthlyGrid.kpi.work', target: targetKpis?.work ?? null },
    { key: 'love' as const, labelKey: 'dashboard.monthlyGrid.kpi.love', target: targetKpis?.love ?? null },
  ];
  return (
    <>
      <GroupRibbon group={group} days={days} todayIndex={todayIndex} />
      {rows.map((row, rowIdx) => {
        const banded = rowIdx % 2 === 1;
        return (
          <tr key={row.key}>
            <td
              className={`sticky left-0 z-10 pl-s4 pr-s3 py-s2 text-ink text-sm font-medium ${
                banded ? BAND_CLASS : 'bg-bone-2'
              }`}
            >
              {t(row.labelKey)}
              {row.target !== null && (
                <span className="ml-1 text-gray-2 font-normal" data-testid={`kpi-target-${row.key}`}>
                  ({row.target})
                </span>
              )}
            </td>
            {days.map((iso, idx) => {
              const point = kpiByDate.get(iso);
              const val = point ? (point[row.key] ?? null) : null;
              const isToday = idx === todayIndex;
              return (
                <td
                  key={iso}
                  className={`text-center px-1 py-s2 ${
                    isToday ? 'bg-flame-glow/30' : banded ? BAND_CLASS : ''
                  }`}
                >
                  {val !== null ? (
                    <span
                      className={`inline-block min-w-[20px] px-1 py-[1px] rounded-md font-mono text-[11px] ${kpiCellClass(val)}`}
                    >
                      {val}
                    </span>
                  ) : (
                    <span className="text-bone-3">·</span>
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

function TrackerGroup({
  group,
  emptyLabel,
  items,
  days,
  todayIndex,
  valuesByDateAndItem,
}: {
  group: GroupVisual;
  emptyLabel: string;
  items: TrackingItem[];
  days: string[];
  todayIndex: number;
  valuesByDateAndItem: Map<string, Map<string, string>>;
}): JSX.Element {
  return (
    <>
      <GroupRibbon group={group} days={days} todayIndex={todayIndex} />
      {items.length === 0 ? (
        // Per-day cells (instead of colSpan) so today's vertical band
        // stays continuous through the empty section.
        <tr>
          <td className="sticky left-0 z-10 bg-bone-2 pl-s5 py-s3 text-sm text-gray-2 italic">
            {emptyLabel}
          </td>
          {days.map((_iso, idx) => (
            <td
              key={`${group.key}-empty-${idx}`}
              className={`py-s3 ${idx === todayIndex ? 'bg-flame-glow/30' : ''}`}
              aria-hidden
            />
          ))}
        </tr>
      ) : (
        items.map((item, rowIdx) => {
          const banded = rowIdx % 2 === 1;
          // F-031 — distinct axes this item is bound to (e.g. ['energy','work']).
          // Rendered as small icons to the right of the label so the
          // client sees at a glance which axis the row serves.
          const axes: VirtueAxis[] = (['energy', 'work', 'love'] as const).filter(
            (axis) => item.virtues?.some((v) => v.axis === axis),
          );
          return (
            <tr key={item.id}>
              <td
                className={`sticky left-0 z-10 pl-s4 pr-s3 py-s2 text-ink text-sm truncate max-w-[220px] ${
                  banded ? BAND_CLASS : 'bg-bone-2'
                }`}
                title={item.label}
              >
                <span className="inline-flex items-center gap-1 align-middle">
                  <span className="truncate max-w-[160px]">{item.label}</span>
                  {axes.map((axis) => (
                    <AxisIcon key={axis} axis={axis} size={12} />
                  ))}
                </span>
              </td>
              {days.map((iso, idx) => {
                const active = isActiveOn(item, iso);
                const value = valuesByDateAndItem.get(iso)?.get(item.id);
                const isToday = idx === todayIndex;
                return (
                  <td
                    key={iso}
                    className={`text-center px-1 py-s2 ${
                      isToday ? 'bg-flame-glow/30' : banded ? BAND_CLASS : ''
                    }`}
                  >
                    {!active ? (
                      <span className="text-bone-3">·</span>
                    ) : item.itemType === 'yes_no' ? (
                      value === 'true' ? (
                        <span
                          aria-hidden
                          className="inline-block w-3.5 h-3.5 rounded-full bg-flame shadow-[0_0_6px_rgba(255,131,0,0.55)]"
                        />
                      ) : (
                        <span
                          aria-hidden
                          className="inline-block w-3.5 h-3.5 rounded-full border border-bone-3 bg-bone-2"
                        />
                      )
                    ) : value && Number.parseInt(value, 10) > 0 ? (
                      <span className="inline-block min-w-[22px] px-1 py-[1px] rounded-md bg-flame-glow/50 font-mono text-[11px] text-flame-deep font-semibold">
                        {Number.parseInt(value, 10)}
                      </span>
                    ) : (
                      <span className="text-bone-3">·</span>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })
      )}
    </>
  );
}
